import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Lock, Unlock } from "@tamagui/lucide-icons";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { ToggleGroup, YStack } from "tamagui";
import { useApi } from "../core/apis";
import { ToggleGroupItem } from "../core/components/ToggleGroupItem";
import { getDayJsDateAndTimeWithFormat } from "../core/dayJs";
import { useStores } from "../core/stores";
import { Body } from "../ui/Body";
import { Box } from "../ui/Box";
import { Button } from "../ui/Button";
import { Group } from "../ui/Group";
import { Label } from "../ui/Label";
import { Screen } from "../ui/Screen";
import { TipsText } from "../ui/TipsText";
import { availableDateFormats, type DateFormat, StartOfWeek } from "./types";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  "PlanSettings"
>;

export const PlanSettingsScreen = observer(({ route }: Props) => {
  const planUid = route.params.planUid;

  const { plansStore } = useStores();
  const { plansApi } = useApi();

  const plan = plansStore.byId.get(planUid);

  React.useEffect(() => {
    const unsub = plansApi.watchItem(planUid);
    return () => {
      unsub();
    };
  }, [plansApi, planUid]);

  if (!plan) {
    return null;
  }

  const isMonday = plan.data.settings.startOfWeek === StartOfWeek.Monday;
  const isLocked = plan.data.settings.isLocked;
  const currentDateFormat = plan.data.settings.dateFormat;
  const planUsesSections = plan.data.settings.useSections;

  const isQuickstart = plan.isQuickstart;

  return (
    <Screen>
      <Box>
        <Body>
          Configure your Planira so it suits your whole groups requirements
        </Body>

        <Group borderTop={false}>
          <YStack gap="$medium">
            <Label>Lock your Planira</Label>
            <Body>
              If you want to make sure noone else can join your Planira you can
              lock it
            </Body>
            <Button
              type={isLocked ? "danger" : "primary"}
              onPress={() => plan.toggleLock()}
              icon={isLocked ? Unlock : Lock}
            >
              {isLocked ? "Unlock Planira" : "Lock Planira"}
            </Button>
            {isLocked && <TipsText text={"This Planira is currently locked"} />}
          </YStack>

          <YStack gap="$medium">
            <Label>Start of week</Label>
            <ToggleGroup
              type="single"
              value={isMonday ? "monday" : "sunday"}
              onValueChange={action((value: StartOfWeek) => {
                plan.setStartOfWeek(value);
              })}
              disablePassBorderRadius
              padding={1}
            >
              <ToggleGroupItem active={isMonday} value={"monday"}>
                <Body>Monday</Body>
              </ToggleGroupItem>
              <ToggleGroupItem active={!isMonday} value={"sunday"}>
                <Body>Sunday</Body>
              </ToggleGroupItem>
            </ToggleGroup>
          </YStack>

          <YStack gap="$medium">
            <Label>Date format</Label>
            <ToggleGroup
              type="single"
              orientation="vertical"
              value={currentDateFormat}
              onValueChange={action((value: DateFormat) => {
                plan.setDateFormat(value);
              })}
              disablePassBorderRadius
              padding={1}
            >
              {availableDateFormats.map((dateFormat) => (
                <ToggleGroupItem
                  key={dateFormat}
                  active={dateFormat === currentDateFormat}
                  value={dateFormat}
                >
                  <Body>
                    {getDayJsDateAndTimeWithFormat(new Date(), dateFormat)}
                  </Body>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </YStack>

          {!isQuickstart && (
            <YStack gap="$medium">
              <Label>Use sections</Label>
              <TipsText text="When enabled you can move cards between sections, to group them in any way you need" />
              <ToggleGroup
                alignSelf="flex-start"
                type="single"
                value={planUsesSections ? "yes" : "no"}
                onValueChange={action((value) => {
                  plan.setUsesSections(value === "yes");
                })}
                disablePassBorderRadius
                padding={1}
              >
                <ToggleGroupItem active={!planUsesSections} value={"no"}>
                  <Body>No, thank you</Body>
                </ToggleGroupItem>
                <ToggleGroupItem active={planUsesSections} value={"yes"}>
                  <Body>Yes, please</Body>
                </ToggleGroupItem>
              </ToggleGroup>
            </YStack>
          )}

          <YStack gap="$medium">
            <Label>Language</Label>
            <Body fontStyle="italic">Upcoming setting!</Body>
          </YStack>
        </Group>
      </Box>
    </Screen>
  );
});

export default PlanSettingsScreen;
